/**
 * PlayerBar Component
 * Bottom playback control bar with track info, controls, and volume
 *
 * Phase 3 COMPLETED: Migrated to unified playback queue system
 * - Uses playbackQueue.previewMode instead of player.isHookmodeEnabled
 * - Timer management uses playbackQueue.clearHookTimer/setHookTimer
 * - Single queue for both hookmode and normal playback
 * - Preview mode determines playback style (full vs 15s hooks)
 */

import { useState, useEffect, useRef } from 'react';
import {
  PlayCircle,
  PauseCircle,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Monitor,
  Power,
  MessageSquareQuote,
} from 'lucide-react';
import { usePlaybackState, usePlayerControls, setPlayerActivateFn, usePlayerWebSocketManager } from '@/api/hooks';
import { useProgressInterpolation } from '@/api/hooks/useProgressInterpolation';
import { DeviceSelector } from '@/components/player/DeviceSelector';
import { usePlayerUIStore } from '@/stores/player';
import { usePlaybackQueueStore } from '@/stores/playbackQueue';
import { useWorkbenchStore } from '@/stores/workbench';
import { buildSmartPlaybackParams } from '@/lib/keyboard-helpers';

const INACTIVE_TIMEOUT_MS = 60 * 1000; // 1 minute

export function PlayerBar() {
  // Player UI state from global store
  const {
    isPlayerActive,
    setIsPlayerActive,
    showDeviceSelector,
    setShowDeviceSelector,
  } = usePlayerUIStore();

  const inactivityTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // WebSocket connection manager - ONLY instance that creates WebSocket connection
  usePlayerWebSocketManager(isPlayerActive);

  // Player state from API (only poll when active AND WebSocket not connected)
  const { data: playbackState } = usePlaybackState({ enabled: isPlayerActive });
  const { play, pause, next, previous, seek, setVolume } = usePlayerControls();

  // Playback queue store
  const playbackQueue = usePlaybackQueueStore();
  const {
    previewMode,
    setPreviewMode,
    setHookTimer,
    clearHookTimer,
  } = playbackQueue;
  const isSingleTrackMode = playbackQueue.source === 'single';

  // Volume state (synced from device)
  const [volume, setVolumeState] = useState(75);
  const [isMuted, setIsMuted] = useState(false);

  // Seekbar state
  const NEAR_THRESHOLD_PERCENT = 5; // Within 5% = "near" playhead (for drag)
  const seekbarRef = useRef<HTMLDivElement>(null);
  const dragSeekPercentRef = useRef(0); // Ref for tracking during drag
  const wasDraggingRef = useRef(false); // Prevent click after drag
  const [isSeekDragging, setIsSeekDragging] = useState(false);
  const [localSeekPercent, setLocalSeekPercent] = useState(0);
  const [targetSeekPercent, setTargetSeekPercent] = useState<number | null>(null); // Target for optimistic update
  const [isNearPlayhead, setIsNearPlayhead] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  // Extract data from playback state
  const isPlaying = playbackState?.isPlaying ?? false;
  const currentTrack = playbackState?.item ?? null;
  const device = playbackState?.device ?? null;

  // Sync volume from device when it changes
  useEffect(() => {
    if (device?.volumePercent !== null && device?.volumePercent !== undefined) {
      setVolumeState(device.volumePercent);
      setIsMuted(device.volumePercent === 0);
    }
  }, [device?.volumePercent]);

  // Use interpolated progress for smooth 60fps updates
  const interpolatedProgress = useProgressInterpolation({
    state: playbackState,
    enabled: isPlayerActive, // Only interpolate when player is active
  });

  // Use interpolated progress when available, fallback to server progress
  const progressMs = interpolatedProgress;

  // Calculate progress percentage
  const durationMs = currentTrack?.durationMs ?? 1;
  const progressPercent = (progressMs / durationMs) * 100;

  // Release optimistic state when server catches up to target
  useEffect(() => {
    if (targetSeekPercent !== null && !isSeekDragging) {
      const diff = Math.abs(progressPercent - targetSeekPercent);
      // Server is within 3% of target - release optimistic state
      if (diff < 3) {
        setTargetSeekPercent(null);
      }
    }
  }, [progressPercent, isSeekDragging, targetSeekPercent]);

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseInt(e.target.value, 10);
    setVolumeState(newVolume);
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
    // Send to Spotify API
    setVolume.mutate({ volumePercent: newVolume });
  };

  const toggleMute = () => {
    const newMuteState = !isMuted;
    setIsMuted(newMuteState);
    const newVolume = newMuteState ? 0 : (volume > 0 ? volume : 50);
    setVolumeState(newVolume);
    setVolume.mutate({ volumePercent: newVolume });
  };

  // Helper: get click position as percentage
  const getClickPercent = (clientX: number): number => {
    if (!seekbarRef.current) return 0;
    const rect = seekbarRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
  };

  // Helper: check if position is near playhead
  const checkNearPlayhead = (clickPercent: number): boolean => {
    return Math.abs(clickPercent - progressPercent) <= NEAR_THRESHOLD_PERCENT;
  };

  // Track mouse position for hover state (show thumb only near playhead)
  const handleSeekbarMouseMove = (e: React.MouseEvent) => {
    if (isSeekDragging) return;
    const clickPercent = getClickPercent(e.clientX);
    setIsNearPlayhead(checkNearPlayhead(clickPercent));
  };

  const handleSeekbarMouseEnter = () => {
    setIsHovering(true);
  };

  const handleSeekbarMouseLeave = () => {
    setIsHovering(false);
    if (!isSeekDragging) {
      setIsNearPlayhead(false);
    }
  };

  // Handle click on seekbar - always seek to clicked position
  const handleSeekbarClick = (e: React.MouseEvent) => {
    // Skip click if we just finished dragging
    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      return;
    }
    if (!currentTrack || seek.isPending) return;

    const clickPercent = getClickPercent(e.clientX);

    // Optimistic update - set target and local position
    setLocalSeekPercent(clickPercent);
    setTargetSeekPercent(clickPercent);

    const positionMs = Math.floor((clickPercent / 100) * durationMs);
    seek.mutate({ positionMs });
  };

  // Handle drag start (only allowed near playhead)
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (!currentTrack || seek.isPending) return;

    const clientX = 'touches' in e ? e.touches[0]?.clientX ?? 0 : e.clientX;
    const clickPercent = getClickPercent(clientX);

    if (checkNearPlayhead(clickPercent)) {
      e.preventDefault(); // Prevent click event
      setIsSeekDragging(true);
      setLocalSeekPercent(progressPercent);
      dragSeekPercentRef.current = progressPercent;

      // Add global listeners for drag
      const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
        const moveX = 'touches' in moveEvent ? moveEvent.touches[0]?.clientX ?? 0 : moveEvent.clientX;
        const newPercent = getClickPercent(moveX);
        setLocalSeekPercent(newPercent);
        dragSeekPercentRef.current = newPercent;
      };

      const handleEnd = () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);

        // Mark that we were dragging to prevent click handler
        wasDraggingRef.current = true;

        // Set target for optimistic update and perform seek
        setTargetSeekPercent(dragSeekPercentRef.current);
        setIsSeekDragging(false);
        const positionMs = Math.floor((dragSeekPercentRef.current / 100) * durationMs);
        seek.mutate({ positionMs });
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('touchend', handleEnd);
    }
  };

  // Display values: local when dragging or waiting for server, otherwise interpolated
  const hasPendingSeek = targetSeekPercent !== null;
  const displaySeekPercent = isSeekDragging || hasPendingSeek ? localSeekPercent : progressPercent;
  const displayProgressMs = isSeekDragging || hasPendingSeek
    ? Math.floor((localSeekPercent / 100) * durationMs)
    : progressMs;

  // Activate player
  const activatePlayer = () => {
    setIsPlayerActive(true);
    resetInactivityTimer();
  };

  // Register activate function globally so usePlayerControls can call it
  useEffect(() => {
    setPlayerActivateFn(activatePlayer);
    return () => setPlayerActivateFn(null);
  }, []);

  // Reset inactivity timer
  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      setIsPlayerActive(false);
    }, INACTIVE_TIMEOUT_MS);
  };

  // Manage inactivity timer when playback state changes
  // NOTE: We do NOT auto-activate based on isPlaying to prevent race conditions
  // where TrackTable/useKeyboardShortcuts polling causes unintended reactivation
  const lastPlayingStateRef = useRef<boolean>(false);

  useEffect(() => {
    // Only manage inactivity timer when player is active
    if (!isPlayerActive) {
      // Player inactive - clear any timer and refs
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = undefined;
      }
      lastPlayingStateRef.current = false;
      return;
    }

    const isCurrentlyPlaying = isPlaying;

    // If playing, clear inactivity timer (don't auto-deactivate while music is playing)
    if (isCurrentlyPlaying) {
      lastPlayingStateRef.current = true;
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = undefined;
      }
      return;
    }

    // If playing state changed from playing to paused, start inactivity timer
    if (!isCurrentlyPlaying && lastPlayingStateRef.current) {
      lastPlayingStateRef.current = false;
      resetInactivityTimer();
    }

    // If player is active but not playing and no timer running, start timer
    if (!isCurrentlyPlaying && !inactivityTimerRef.current) {
      resetInactivityTimer();
    }
  }, [isPlaying, isPlayerActive]);

  // Cleanup inactivity timer on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, []);

  // Hookmode playback helper - uses playback queue timer
  const playHookmodeTrack = (hookItem: { uri: string; durationMs: number }) => {
    const hookStart = Math.floor(hookItem.durationMs / 2 - 7500); // 15s centered
    const hookDuration = 15000; // 15 seconds

    // Clear any existing timer using playback queue
    clearHookTimer();

    // Start playback at hook position
    play.mutate({
      uris: [hookItem.uri],
      positionMs: Math.max(0, hookStart),
    });

    // Set timer to auto-advance after 15s
    const timerId = setTimeout(() => {
      const nextItem = playbackQueue.moveNext();
      if (nextItem) {
        playHookmodeTrack(nextItem);
      } else {
        // End of queue - pause playback
        pause.mutate();
      }
    }, hookDuration);

    setHookTimer(timerId);
  };

  // Handle pause with hookmode timer cleanup
  const handlePause = () => {
    clearHookTimer(); // Stop hookmode timer
    pause.mutate();
  };

  // Handle play - starts playlist mode from first track if nothing is playing
  const handlePlay = () => {
    // If already playing something, just resume
    if (currentTrack) {
      play.mutate(undefined);
      return;
    }

    // Nothing playing - start from first workbench track in playlist mode
    const { tracks, activePlaylist, isOrderUnchanged } = useWorkbenchStore.getState();
    if (tracks.length === 0) {
      play.mutate(undefined); // Fallback to resume
      return;
    }

    // Build queue from all tracks
    const queueItems = tracks.map(t => ({
      uri: t.uri,
      id: t.id,
      durationMs: t.durationMs,
    }));

    // Smart playback: use contextUri+offset when list is unchanged (fast)
    const playbackParams = buildSmartPlaybackParams(
      activePlaylist?.uri,
      0, // Start from first track
      queueItems.map(i => i.uri),
      isOrderUnchanged()
    );

    playbackQueue.setQueue(queueItems, 0, 'playlist', playbackParams.isContextMode);
    play.mutate(playbackParams);
  };

  // Handle next - hookmode and context-mode aware
  const handleNext = () => {
    if (previewMode === 'hook' && playbackQueue.items.length > 0) {
      // Hookmode: Move to next in queue and play hook
      const nextItem = playbackQueue.moveNext();
      if (nextItem) {
        playHookmodeTrack(nextItem);
      } else {
        // At end of queue - pause
        pause.mutate();
        playbackQueue.clear();
      }
    } else if (playbackQueue.isContextMode) {
      // Context mode: Spotify manages the queue, use native API
      next.mutate();
    } else {
      // Manual queue mode: Use playback queue
      const nextItem = playbackQueue.moveNext();
      if (nextItem) {
        play.mutate({ uris: [nextItem.uri] });
      } else {
        // End of queue - pause and clear
        pause.mutate();
        playbackQueue.clear();
      }
    }
  };

  // Handle previous - hookmode and context-mode aware
  const handlePrevious = () => {
    if (previewMode === 'hook' && playbackQueue.items.length > 0) {
      // Hookmode: Move to previous in queue
      const prevItem = playbackQueue.movePrevious();
      if (prevItem) {
        playHookmodeTrack(prevItem);
      }
      // If at start, do nothing (or could restart current hook)
    } else if (playbackQueue.isContextMode) {
      // Context mode: Spotify manages the queue, use native API
      previous.mutate();
    } else {
      // Manual queue mode: Use playback queue
      const prevItem = playbackQueue.movePrevious();
      if (prevItem) {
        play.mutate({ uris: [prevItem.uri] });
      } else {
        // At start of queue - seek to beginning (using previous API)
        previous.mutate();
      }
    }
  };

  // Handle hookmode toggle - rebuild queues if playback is active
  const handleHookmodeToggle = () => {
    const newMode = previewMode === 'hook' ? 'full' : 'hook';

    // If playback is active and we have a current track playing
    if (currentTrack) {
      // Get all tracks from workbench
      const tracks = useWorkbenchStore.getState().tracks;
      const currentIndex = tracks.findIndex(t => t.id === currentTrack.id);

      if (currentIndex !== -1) {
        // Rebuild queue from current position with same items
        const queueItems = tracks.map(t => ({
          uri: t.uri,
          id: t.id,
          durationMs: t.durationMs,
        }));

        // Keep the same queue, just change the mode
        playbackQueue.setQueue(queueItems, currentIndex, playbackQueue.source);

        if (newMode === 'full') {
          // Switching FROM hookmode - clear hookmode timer
          clearHookTimer();
        }
      }
    }

    // Always set the new preview mode
    setPreviewMode(newMode);
  };

  return (
    <footer className="h-20 border-t border-zinc-800 bg-zinc-900 relative">
      {/* Inactive Overlay */}
      {!isPlayerActive && (
        <div className="absolute inset-0 bg-zinc-900/80 backdrop-blur-sm z-10 flex items-center justify-center">
          <button
            onClick={activatePlayer}
            className="flex items-center gap-3 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors shadow-lg"
          >
            <Power className="w-5 h-5" />
            <span className="font-medium">Activate Player</span>
          </button>
        </div>
      )}

      <div className="h-full grid grid-cols-3 items-center px-4 gap-4">
        {/* Left: Current Track Info */}
        <div className="flex items-center gap-3 min-w-0">
          {currentTrack?.album?.images?.[0]?.url ? (
            <img
              src={currentTrack.album.images[0].url}
              alt={currentTrack.name}
              className="w-14 h-14 rounded-md object-cover"
            />
          ) : (
            <div className="w-14 h-14 bg-zinc-800 rounded-md flex items-center justify-center">
              <PlayCircle className="w-6 h-6 text-zinc-600" />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-zinc-100 truncate">
              {currentTrack?.name ?? 'No track playing'}
            </p>
            <p className="text-xs text-zinc-400 truncate">
              {currentTrack?.artists?.map(a => a.name).join(', ') ?? 'Select a track to play'}
            </p>
          </div>
        </div>

        {/* Center: Playback Controls + Progress */}
        <div className="flex flex-col items-center gap-0">
          {/* Control Buttons */}
          <div className="flex items-center gap-2">
            {!isSingleTrackMode && (
              <button
                onClick={handlePrevious}
                className="p-2 text-zinc-400 hover:text-zinc-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous track"
                disabled={previous.isPending}
                title={previewMode === 'hook' ? 'Previous hook' : 'Previous track'}
              >
                <SkipBack className="w-5 h-5" />
              </button>
            )}

            <button
              onClick={() => isPlaying ? handlePause() : handlePlay()}
              className="p-2 text-zinc-100 hover:text-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={isPlaying ? 'Pause' : 'Play'}
              disabled={play.isPending || pause.isPending}
            >
              {isPlaying ? (
                <PauseCircle className="w-8 h-8" />
              ) : (
                <PlayCircle className="w-8 h-8" />
              )}
            </button>

            {!isSingleTrackMode && (
              <button
                onClick={handleNext}
                className="p-2 text-zinc-400 hover:text-zinc-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next track"
                disabled={next.isPending || (previewMode !== 'hook' && !isPlaying)}
                title={previewMode === 'hook' ? 'Next hook' : 'Next track'}
              >
                <SkipForward className="w-5 h-5" />
              </button>
            )}

            {/* Hookmode Toggle Icon */}
            <button
              onClick={handleHookmodeToggle}
              className={`p-2 transition-colors ${
                previewMode === 'hook'
                  ? 'text-emerald-400 hover:text-emerald-300'
                  : 'text-zinc-400 hover:text-zinc-100'
              }`}
              aria-label="Toggle hook mode"
              title={previewMode === 'hook' ? 'Disable Hook Mode (15s previews)' : 'Enable Hook Mode (15s previews)'}
            >
              <MessageSquareQuote className="w-5 h-5" />
            </button>
          </div>

          {/* Play Indicator + Progress Bar Container */}
          <div className="w-full flex flex-col items-center gap-1">
            {/* Progress Bar */}
            <div className="flex items-center gap-2 w-full max-w-lg mx-auto">
              <span className="text-xs text-zinc-400 tabular-nums">
                {formatTime(displayProgressMs)}
              </span>

              {/* Custom Seekbar */}
              <div
                ref={seekbarRef}
                className="flex-1 h-4 flex items-center cursor-pointer"
                onMouseEnter={handleSeekbarMouseEnter}
                onMouseMove={handleSeekbarMouseMove}
                onMouseLeave={handleSeekbarMouseLeave}
                onClick={handleSeekbarClick}
                onMouseDown={handleDragStart}
                onTouchStart={handleDragStart}
                role="slider"
                aria-label="Seek position"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={displaySeekPercent}
                tabIndex={currentTrack ? 0 : -1}
              >
                {/* Track background */}
                <div className="w-full h-1 bg-zinc-700 rounded-full relative">
                  {/* Progress fill - white normally, green on hover */}
                  <div
                    className={`h-full rounded-full transition-colors duration-150 ${
                      isHovering ? 'bg-emerald-500' : 'bg-zinc-100'
                    }`}
                    style={{ width: `${displaySeekPercent}%` }}
                  />
                  {/* Playhead thumb - only visible when near playhead or dragging */}
                  <div
                    className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-zinc-100 rounded-full shadow-lg
                      transition-all duration-150 pointer-events-none
                      ${isNearPlayhead || isSeekDragging ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}
                      ${isSeekDragging ? 'scale-125' : ''}
                    `}
                    style={{ left: `calc(${displaySeekPercent}% - 6px)` }}
                  />
                </div>
              </div>

              <span className="text-xs text-zinc-400 tabular-nums">
                {formatTime(durationMs)}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Volume + Device Selector */}
        <div className="flex items-center justify-end gap-4">
          {/* Volume Control */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMute}
              className="p-2 text-zinc-400 hover:text-zinc-100 transition-colors"
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="w-5 h-5" />
              ) : (
                <Volume2 className="w-5 h-5" />
              )}
            </button>

            <input
              type="range"
              min="0"
              max="100"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="w-24 h-1 bg-zinc-700 rounded-full appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-3
                [&::-webkit-slider-thumb]:h-3
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-zinc-100
                [&::-webkit-slider-thumb]:cursor-pointer
                hover:[&::-webkit-slider-thumb]:bg-emerald-400
                [&::-moz-range-thumb]:w-3
                [&::-moz-range-thumb]:h-3
                [&::-moz-range-thumb]:rounded-full
                [&::-moz-range-thumb]:bg-zinc-100
                [&::-moz-range-thumb]:border-0
                [&::-moz-range-thumb]:cursor-pointer
                hover:[&::-moz-range-thumb]:bg-emerald-400"
              aria-label="Volume"
            />
          </div>

          {/* Device Selector */}
          <button
            onClick={() => setShowDeviceSelector(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Select playback device"
          >
            <Monitor className="w-4 h-4" />
            <span className="text-sm">{device?.name ?? 'No Device'}</span>
          </button>
        </div>
      </div>

      {/* Device Selector Modal */}
      <DeviceSelector
        isOpen={showDeviceSelector}
        onClose={() => setShowDeviceSelector(false)}
      />
    </footer>
  );
}
